import { memo } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { EmployeeGrowthReport } from 'types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

/**
 * Format large numbers to a more readable format (B for billion, M for million, K for thousand)
 * @param value The number to format
 * @returns Formatted string
 */
const formatYAxisLabel = (value: number): string => {
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  } else if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

const options = {
  plugins: {
    legend: {
      display: false,
      position: 'bottom' as const,
    },
    tooltip: {
      callbacks: {
        label: function (context: any) {
          const label = context.dataset.label || '';
          const value = context.raw;
          // Display the full, unformatted value in the tooltip
          return `${label}: ${value.toLocaleString()}`;
        },
      },
    },
    datalabels: {
      color: '#124EDB',
      anchor: 'end' as const,
      align: 'top' as const,
      offset: -2,
      font: {
        weight: 'bold' as const,
        size: 11,
      },
      formatter: (value: number) => formatYAxisLabel(value),
      display: (context: any) => {
        // Only show labels for data points that are not zero
        return context.dataset.data[context.dataIndex] > 0;
      },
    },
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        borderDash: [8, 6],
        color: '#CDD5DF',
      },
      ticks: {
        callback: function (value: number) {
          return formatYAxisLabel(value);
        },
      },
    },
  },
  borderWidth: 1.5,
  pointRadius: 4,
  pointBackgroundColor: '#fff',
};

interface EmployeeGrowthChartProps {
  loading: boolean;
  dataChart: EmployeeGrowthReport;
  onLineClick?: (label: string, type: string) => void;
}

const StyledWrapperChart = styled.div`
  width: 100%;
  height: 290px;
`;

function PayrollSummaryChart({ loading, dataChart, onLineClick }: EmployeeGrowthChartProps) {
  return (
    <Spin spinning={loading}>
      <StyledWrapperChart>
        <Line
          data={dataChart}
          options={options}
          getElementAtEvent={(elements, event) => {
            if (event.type === 'click' && elements.length) {
              const label = dataChart.labels[elements[0].index];
              const type = dataChart.datasets[elements[0].datasetIndex].label;

              onLineClick?.(label, type);
            }
          }}
        />
      </StyledWrapperChart>
    </Spin>
  );
}

export default memo(PayrollSummaryChart);
